import { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { AuthContext } from '@/App';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Brain, Sparkles, TrendingUp, AlertCircle, Loader2, Facebook, Chrome } from 'lucide-react';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import ReactMarkdown from 'react-markdown';
import { useTheme } from '@/contexts/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AIInsights() {
  const { token } = useContext(AuthContext);
  const { clientId } = useWorkspace();
  const clientIdRef = useRef(clientId);
  useEffect(() => { clientIdRef.current = clientId; }, [clientId]);
  const theme = useTheme();
  const [insights, setInsights] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [analysisType, setAnalysisType] = useState('performance');
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [insightsRes, campaignsRes] = await Promise.all([
        axios.get(`${API}/ai/insights${clientId ? `?client_id=${clientId}` : ''}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/campaigns${clientId ? `?client_id=${clientId}` : ''}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setInsights(insightsRes.data);
      setCampaigns(campaignsRes.data);
      
      // Auto-select active campaigns
      const activeCampaigns = campaignsRes.data
        .filter(c => c.status === 'active')
        .map(c => c.id)
        .slice(0, 3);
      setSelectedCampaigns(activeCampaigns);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyze = async () => {
    if (selectedCampaigns.length === 0) {
      toast.error('Please select at least one campaign');
      return;
    }

    setAnalyzing(true);
    try {
      const response = await axios.post(
        `${API}/ai/analyze`,
        {
          campaign_ids: selectedCampaigns,
          analysis_type: analysisType,
          client_id: clientId
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('AI analysis completed!');
      fetchData(); // Refresh insights
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const toggleCampaignSelection = (campaignId) => {
    setSelectedCampaigns(prev => 
      prev.includes(campaignId)
        ? prev.filter(id => id !== campaignId)
        : [...prev, campaignId]
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-primary text-xl">Loading AI insights...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-8" data-testid="ai-insights-page">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-ai-glow to-brand-meta flex items-center justify-center">
              <Brain className="w-6 h-6 text-white" />
            </div>
            <h1 className="font-heading font-bold text-4xl">AI Insights</h1>
          </div>
          <p className="text-secondary">Get intelligent recommendations powered by GPT-4o</p>
        </motion.div>

        {/* Analysis Panel */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-8 mb-8 border border-ai-glow/20"
          style={{ boxShadow: '0 0 40px rgba(217, 70, 239, 0.15)' }}
          data-testid="analysis-panel"
        >
          <div className="flex items-center space-x-2 mb-6">
            <Sparkles className="w-6 h-6 text-ai-glow" />
            <h3 className="font-heading font-semibold text-2xl">Generate New Analysis</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Analysis Type Selection */}
            <div>
              <label className={`font-mono text-xs uppercase tracking-widest mb-3 block ${theme.isDark ? 'text-secondary' : 'text-gray-600'}`}>
                Analysis Type
              </label>
              <Select value={analysisType} onValueChange={setAnalysisType}>
                <SelectTrigger className={`h-12 ${theme.isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'}`} data-testid="analysis-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className={theme.isDark ? 'bg-card border-white/10 text-white' : 'bg-white border-gray-200 text-gray-900'}>
                  <SelectItem value="performance">Performance Analysis</SelectItem>
                  <SelectItem value="optimization">Optimization Suggestions</SelectItem>
                  <SelectItem value="comparison">Platform Comparison</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Campaign Selection */}
          <div className="mb-6">
            <label className={`font-mono text-xs uppercase tracking-widest mb-3 block ${theme.isDark ? 'text-secondary' : 'text-gray-600'}`}>
              Select Campaigns to Analyze
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {campaigns.map((campaign) => (
                <button
                  key={campaign.id}
                  onClick={() => toggleCampaignSelection(campaign.id)}
                  className={`p-4 rounded-lg border transition-all ${
                    selectedCampaigns.includes(campaign.id)
                      ? theme.isDark 
                        ? 'bg-ai-glow/20 border-ai-glow text-white' 
                        : 'bg-purple-100 border-purple-500 text-gray-900'
                      : theme.isDark 
                        ? 'bg-white/5 border-white/10 text-secondary hover:border-white/20'
                        : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
                  }`}
                  data-testid={`campaign-select-${campaign.id}`}
                >
                  <div className="flex items-center space-x-2">
                    {campaign.platform === 'meta' ? (
                      <Facebook className="w-4 h-4 text-brand-meta" />
                    ) : (
                      <Chrome className="w-4 h-4 text-brand-google" />
                    )}
                    <p className={`font-medium text-sm truncate ${theme.isDark ? '' : 'text-gray-900'}`}>{campaign.name}</p>
                  </div>
                  <p className={`text-xs mt-1 capitalize ${theme.isDark ? 'opacity-60' : 'text-gray-500'}`}>{campaign.platform}</p>
                </button>
              ))}
            </div>
          </div>

          <Button
            onClick={handleAnalyze}
            disabled={analyzing || selectedCampaigns.length === 0}
            className="bg-gradient-to-r from-ai-glow to-brand-meta text-white hover:opacity-90 rounded-full px-8 h-12"
            data-testid="generate-analysis-button"
          >
            {analyzing ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Analyzing with AI...
              </>
            ) : (
              <>
                <Brain className="w-5 h-5 mr-2" />
                Generate AI Analysis
              </>
            )}
          </Button>
        </motion.div>

        {/* Insights List */}
        <div className="space-y-4">
          <h3 className="font-heading font-semibold text-2xl mb-4">Recent Insights</h3>
          
          {insights.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="glass-card p-12 text-center"
            >
              <Brain className="w-16 h-16 mx-auto mb-4 text-secondary" />
              <p className="text-secondary text-lg">No insights yet. Generate your first AI analysis above!</p>
            </motion.div>
          ) : (
            insights.map((insight, index) => (
              <motion.div
                key={insight.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="glass-card p-6 hover:border-ai-glow/30 transition-colors"
                data-testid={`insight-card-${index}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    {insight.priority === 'high' ? (
                      <div className="w-10 h-10 rounded-lg bg-error/20 flex items-center justify-center">
                        <AlertCircle className="w-5 h-5 text-error" />
                      </div>
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-ai-glow/20 flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 text-ai-glow" />
                      </div>
                    )}
                    <div>
                      <h4 className="font-heading font-semibold text-lg">{insight.title}</h4>
                      <p className="text-xs text-secondary">
                        {new Date(insight.created_at).toLocaleDateString()} at {new Date(insight.created_at).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    insight.priority === 'high'
                      ? 'bg-error/20 text-error'
                      : insight.priority === 'medium'
                      ? 'bg-warning/20 text-warning'
                      : 'bg-success/20 text-success'
                  }`}>
                    {insight.priority} priority
                  </span>
                </div>
                <p className="text-secondary leading-relaxed">
                  <ReactMarkdown 
                    components={{
                      p: ({children}) => <p className="mb-3">{children}</p>,
                      strong: ({children}) => <strong className="text-white font-semibold">{children}</strong>,
                      ul: ({children}) => <ul className="list-disc list-inside mb-3 space-y-1">{children}</ul>,
                      ol: ({children}) => <ol className="list-decimal list-inside mb-3 space-y-1">{children}</ol>,
                      li: ({children}) => <li className="text-secondary">{children}</li>,
                      h1: ({children}) => <h1 className="text-xl font-bold text-white mb-2">{children}</h1>,
                      h2: ({children}) => <h2 className="text-lg font-semibold text-white mb-2">{children}</h2>,
                      h3: ({children}) => <h3 className="text-base font-semibold text-white mb-2">{children}</h3>,
                      hr: () => <hr className="border-white/10 my-4" />
                    }}
                  >
                    {insight.description}
                  </ReactMarkdown>
                </p>
              </motion.div>
            ))
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
